import { useEdgesState, useNodesState, useReactFlow, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { ModalCloseIcon } from '../../assets/icons/common/common-icons';
import { BaseModal } from '../../common/components/BaseModal/BaseModal';
import { PrimaryButton, SecondaryButton } from '../../common/components/buttons/NewButtons.styles';
import { useStore } from '../app/data/root.store';
import { DeleteHoldingEntity } from '../user/components/HoldingEntityList/DeleteHoldingEntity';
import { BottomControlsContainer } from './EntitiesPage.styles';
import { BottomControls } from './components/BottomControls/BottomControls';
import { EntitiesChart } from './components/EntitiesChart/EntitiesChart';
import { EntitiesHeader } from './components/EntitiesHeader/EntitiesHeader';
import { EntitiesPanel } from './components/EntitiesPanel/EntitiesPanel';
import { EntityStructureReport } from './components/EntityStructureReport';
import { downloadImage, isEdgeValid } from './utils/graphUtils';
import { useAnalytics } from '../app/data/AnalyticsProvider';
import { createEntityManagementCTAClickEvent } from './data/entities.mixpanel-events';
import { EditHoldingEntity } from './EditHoldingEntity/EditHoldingEntity';

export const EntitiesFlowContent = observer(() => {
  const { entitiesStore, uiStore, userStore, metadataStore } = useStore();
  const { fitView, zoomIn, zoomOut, getNodes } = useReactFlow();
  
  const isInteractionBlocked = entitiesStore.isCanvasLocked || userStore.isDemoMode || userStore.isReadOnlyMode || userStore.isActionBlockedByRole('editHoldingEntity');


  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showStructureReport, setShowStructureReport] = useState(false);
  const [showEntityDetail, setShowEntityDetail] = useState(false);
  const { mixpanelTrack } = useAnalytics();

  const {
    selectedEntityId,
    sortedEntities: entities,
    selectedEntity,
    showAddModal,
    showEditModal,
    showDeleteModal,
    showEntityListModal,
    editingEntity
  } = entitiesStore;



  useEffect(() => {
    const { nodes: initialNodes, edges: initialEdges } = entitiesStore.graphData;
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [entitiesStore.graphData, setNodes, setEdges]);

  useEffect(() => {
    const fetchData = async () => {
      await entitiesStore.fetchData();
      fitView({ duration: 100, padding: 0.3 });
    };
    fetchData();
  }, [entitiesStore, fitView]);

  useEffect(() => {
    const removeTemporaryEdge = (edgeId) => {
      setEdges((eds) => eds.filter(e => e.id !== edgeId));
    };
    entitiesStore.setTemporaryEdgeRemoveCallback(removeTemporaryEdge);
  }, [entitiesStore, setEdges]);

  useEffect(() => {
    const downloadImageCallback = () => {
      downloadImage(getNodes());
    };
    entitiesStore.setDownloadImageCallback(downloadImageCallback);
  }, [entitiesStore, getNodes]);

  useEffect(() => {
    if (!selectedEntityId) {
      setShowEntityDetail(false);
    }
  }, [selectedEntityId]);

  const handleNodeClick = useCallback((_event, node) => {
    if (isInteractionBlocked) return;
    entitiesStore.setSelectedEntityId(node.id);
    setShowEntityDetail(true);
    if (!uiStore.isEntitiesPageThreshold) {
      entitiesStore.setShowEntityListModal(true);
    }
  }, [entitiesStore, isInteractionBlocked, uiStore.isEntitiesPageThreshold]);

  const handleNodeMouseEnter = useCallback((_event, node) => {
    if (isInteractionBlocked) return;
    entitiesStore.setHoveredEntityId(node.id);
  }, [entitiesStore, isInteractionBlocked]);

  const handleNodeMouseLeave = useCallback((_event, node) => {
    if (isInteractionBlocked) return;
    entitiesStore.setHoveredEntityId(null);
  }, [entitiesStore, isInteractionBlocked]);

  const handleConnect = useCallback((params) => {
    const isGhostNode = params.source === 'ghost-add-entity' || params.target === 'ghost-add-entity';
    const edgeIsInvalid = !isEdgeValid(params.source, params.target, edges);
    if (isInteractionBlocked || isGhostNode || edgeIsInvalid) return;

    const tempEdgeId = `temp-${Date.now()}`;
    const tempEdge = {
      id: tempEdgeId,
      source: params.source,
      target: params.target,
      type: 'custom-entity-edge',
      data: {
        source: params.source,
        target: params.target,
        ownership: '',
        label: '',
        percentage: '',
        isNewConnection: true
      },
      label: '',
      animated: true,
      selectable: true,
      deletable: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 15,
        height: 15,
        color: '#ACB1BB'
      }
    };
    setEdges((eds) => [...eds, tempEdge]);

    entitiesStore.setOpenPopoverId(tempEdgeId);
  }, [entitiesStore, isInteractionBlocked, setEdges, edges]);

  const handleEdgeClick = useCallback((_event, edge) => {
    if (isInteractionBlocked || edge.data?.isGhost || edge.id === 'ghost-edge') return;

    entitiesStore.setOpenPopoverId(edge.id);
  }, [entitiesStore, isInteractionBlocked]);

  const handleEdgesChange = useCallback((changes) => {
    if (isInteractionBlocked) return;
    onEdgesChange(changes);
  }, [onEdgesChange, isInteractionBlocked]);

  const handleNodesChange = useCallback(async (changes) => {
    if (isInteractionBlocked || entitiesStore.isUpdatingNodePosition) return;
    const positionChanges = changes.filter(change =>
      change.type === 'position' && change.position && change.dragging === false
    );

    for (const change of positionChanges) {
      if (change.position) {
        entitiesStore.updateNodePosition(change.id, change.position);
      }
    }

    onNodesChange(changes);
  }, [onNodesChange, entitiesStore, isInteractionBlocked]);


  const handleDeleteFromModal = async (entityId) => {
    if (userStore.isDemoMode) {
      uiStore.setShowDemoLimitationModal(true);
      return;
    } else if (userStore.isReadOnlyMode || userStore.isActionBlockedByRole('deleteHoldingEntity')) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }

    await entitiesStore.deleteHoldingEntity(entityId);
    entitiesStore.setShowEditModal(false);
  };

  const handleAddEntity = () => {
    if (userStore.isDemoMode) {
      uiStore.setShowDemoLimitationModal(true);
      return;
    } else if (userStore.isReadOnlyMode || userStore.isActionBlockedByRole('addHoldingEntity')) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }

    entitiesStore.setShowAddModal(true);
  };

  const handleEditEntity = (entity) => {
    if (userStore.isDemoMode) {
      uiStore.setShowDemoLimitationModal(true);
      return;
    } else if (userStore.isReadOnlyMode || userStore.isActionBlockedByRole('editHoldingEntity')) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }

    entitiesStore.setShowEditModal(true, entity);
  };

  const handleDeleteEntity = (entityId) => {
    if (userStore.isDemoMode) {
      uiStore.setShowDemoLimitationModal(true);
      return;
    } else if (userStore.isReadOnlyMode || userStore.isActionBlockedByRole('deleteHoldingEntity')) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }

    entitiesStore.setSelectedEntityId(entityId);
    entitiesStore.setShowDeleteModal(true);
  };

  const handleSaveEntity = async (data) => {
    if (editingEntity) {
      await entitiesStore.updateHoldingEntity(data);
    } else {
      await entitiesStore.createHoldingEntity(data);
      setTimeout(() => {
        fitView({ duration: 500, padding: 0.3 });
      }, 100);
    }
    metadataStore.fetchData();
    entitiesStore.setShowAddModal(false);
  };

  const confirmDelete = async () => {
    if (selectedEntity) {
      await entitiesStore.deleteHoldingEntity(selectedEntity.id);
      entitiesStore.setShowDeleteModal(false);
    }
  };


  const handleEntitySelect = (entityId) => {
    entitiesStore.setSelectedEntityId(entityId);
    setShowEntityDetail(true);
  };

  const handleBackToList = () => {
    setShowEntityDetail(false);
    entitiesStore.setSelectedEntityId(null);
  };


  const handleCloseEntityList = () => {
    entitiesStore.setShowEntityListModal(false);
  };


  const handleRearrangeClick = async () => {
    if (userStore.isActionBlockedByRole('editHoldingEntity') || userStore.isReadOnlyMode) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }
    mixpanelTrack('CTA Clicked', createEntityManagementCTAClickEvent('Rearrange structure', 'Entity Management Bar'));
    await entitiesStore.handleRearrange();
    fitView({ duration: 100, padding: 0.3 });
};

  const isEmptyStateSingleEntity = edges.length === 0 && nodes.length === 1 && !entitiesStore.loading;

  const shouldShowPanelInLayout = uiStore.isEntitiesPageThreshold;

  return (
    <>
      {uiStore.isDesktopView &&
        <EntitiesHeader
          nodes={nodes}
          handleAddEntity={handleAddEntity}
          handleRearrangeClick={handleRearrangeClick}
        />}

      <EntitiesChart
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        handleNodeClick={handleNodeClick}
        handleNodeMouseEnter={handleNodeMouseEnter}
        handleNodeMouseLeave={handleNodeMouseLeave}
        handleConnect={handleConnect}
        handleEdgeClick={handleEdgeClick}
        isEmptyStateSingleEntity={isEmptyStateSingleEntity}
        handleAddEntity={handleAddEntity}
      >
        {uiStore.isDesktopView && <BottomControlsContainer isThreshold={uiStore.isEntitiesPageThreshold}>
          <BottomControls
            fitView={fitView}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
          />
        </BottomControlsContainer>}
      </EntitiesChart>

      {(shouldShowPanelInLayout || !uiStore.isEntitiesPageThreshold) && (
        <EntitiesPanel
          showEntityDetail={showEntityDetail}
          selectedEntity={selectedEntity}
          handleBackToList={handleBackToList}
          handleEditEntity={handleEditEntity}
          handleDeleteEntity={handleDeleteEntity}
          entities={entities}
          selectedEntityId={selectedEntityId}
          handleEntitySelect={handleEntitySelect}
          handleAddEntity={handleAddEntity}
          {...(!uiStore.isEntitiesPageThreshold && {
            isOpen: showEntityListModal,
            onClose: handleCloseEntityList
          })}
        />
      )}

      {showStructureReport && EntityStructureReport && (
        <EntityStructureReport
          entities={entities}
          nodes={nodes}
          edges={edges}
          onClose={() => setShowStructureReport(false)}
        />
      )}

      {showAddModal &&
        <EditHoldingEntity
          onClose={() => entitiesStore.setShowAddModal(false)}
          onSave={handleSaveEntity}
          isEdit={false}
          item={{ type: 'Other', colorCode: '#E2E8F0' }}
          drawerVisible={showAddModal}
        />
      }

      {showEditModal && editingEntity &&
        <EditHoldingEntity
          onClose={() => entitiesStore.setShowEditModal(false)}
          onSave={handleSaveEntity}
          isEdit={true}
          item={editingEntity}
          drawerVisible={showEditModal}
          onDelete={handleDeleteFromModal}
        />
      }

      {showDeleteModal && (
        <BaseModal width='300px' height='auto' borderRadius={8} onBackDropClick={() => entitiesStore.setShowDeleteModal(false)}>
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Confirm Delete</h3>
              <ModalCloseIcon onClick={() => entitiesStore.setShowDeleteModal(false)} style={{ cursor: 'pointer' }} />
            </div>
            <p>Are you sure you want to delete this entity?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <SecondaryButton onClick={() => entitiesStore.setShowDeleteModal(false)}>No</SecondaryButton>
              <PrimaryButton onClick={confirmDelete}>Yes</PrimaryButton>
            </div>
          </div>
        </BaseModal>
      )}

      {showDeleteModal && (
        <DeleteHoldingEntity
          onClose={() => entitiesStore.setShowDeleteModal(false)}
          onDelete={confirmDelete}
          item={selectedEntity}
          relevantImage={selectedEntity?.bgColor}
        />
      )}
    </>
  );
});